import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Loader2, Target } from 'lucide-react';
import {
  marketPredictionService,
  type MarketPredictionResponse,
} from '@/services/api';
import {
  buildMarketPredictionThesis,
  expandSectorTickersInText,
  formatAssetLegLabel,
  formatSectorOutlookLabel,
  forwardDriversFromComposerPayload,
  humanizePredictionLabel,
  INDEX_ETF_LABELS,
  isInternalKeyInput,
  resolveComposerScenarios,
  resolveNextSessionBlock,
  sanitizePredictionProse,
} from '@/utils/marketPredictionCopy';
import { MarketThemeCards } from '@/components/MarketThemeCards';
import { MarketPredictionScorecardPanel } from '@/components/MarketPredictionScorecard';
import { MarketAnalogueAnalysisPanel } from '@/components/MarketAnalogueAnalysisPanel';
import type { MarketAnalysisTheme } from '@/services/api';

function formatDateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const date = new Date(`${value}T12:00:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return value;
  }
}

function formatPct(value: number) {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function formatBps(value: number) {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)} bps`;
}

function pctChipClass(value: number) {
  if (value > 0) return 'bg-emerald-50 text-emerald-800';
  if (value < 0) return 'bg-rose-50 text-rose-800';
  return 'bg-gray-100 text-gray-600';
}

function humanizeKey(value: string): string {
  return value.replace(/_/g, ' ');
}

type ScenarioRow = {
  label: string;
  SPY_pct?: number;
  QQQ_pct?: number;
  DIA_pct?: number;
  IWM_pct?: number;
  rationale?: string;
};

type CatalystScenarioRow = {
  key: string;
  label: string;
  surprise_bucket?: string;
  market_legs: Record<string, number>;
  rationale?: string;
};

type IndexDetail = {
  ticker: string;
  direction?: string;
  predicted_change_pct: number;
  predicted_close?: number;
  predicted_range_pct?: [number, number];
  confidence?: string;
};

type TopAnalog = {
  date: string;
  similarity: number;
  next_date?: string;
  next_SPY?: number;
  next_QQQ?: number;
  next_DIA?: number;
  next_IWM?: number;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function displayProse(value: unknown): string | null {
  const text = asString(value);
  if (!text) return null;
  return expandSectorTickersInText(sanitizePredictionProse(text));
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function parseIndexDetails(indices: Record<string, unknown> | null): IndexDetail[] {
  if (!indices) return [];
  const details: IndexDetail[] = [];
  for (const ticker of ['SPY', 'QQQ', 'DIA', 'IWM'] as const) {
    const row = asRecord(indices[ticker]);
    if (!row) continue;
    const pct = asNumber(row.predicted_change_pct);
    if (pct == null) continue;
    const range = Array.isArray(row.predicted_range_pct) ? row.predicted_range_pct : null;
    const low = range ? asNumber(range[0]) : null;
    const high = range ? asNumber(range[1]) : null;
    details.push({
      ticker,
      direction: asString(row.direction) ?? undefined,
      predicted_change_pct: pct,
      predicted_close: asNumber(row.predicted_close) ?? undefined,
      predicted_range_pct: low != null && high != null ? [low, high] : undefined,
      confidence: asString(row.confidence) ?? undefined,
    });
  }
  return details;
}

function parseCatalystScenarios(
  raw: Record<string, unknown> | null,
): CatalystScenarioRow[] {
  if (!raw) return [];
  const labels: Record<string, string> = {
    hot_cpi: 'Hot CPI',
    inline_cpi: 'Inline CPI',
    cool_cpi: 'Cool CPI',
    bull_case: 'Bull',
    base_case: 'Base',
    bear_case: 'Bear',
  };
  const scenarios: CatalystScenarioRow[] = [];
  for (const [key, value] of Object.entries(raw)) {
    const row = asRecord(value);
    if (!row) continue;
    const legs = asRecord(row.market_legs);
    const marketLegs: Record<string, number> = {};
    if (legs) {
      for (const [leg, legValue] of Object.entries(legs)) {
        const num = asNumber(legValue);
        if (num != null) marketLegs[leg] = num;
      }
    }
    scenarios.push({
      key,
      label: labels[key] ?? humanizeKey(key),
      surprise_bucket: asString(row.surprise_bucket) ?? undefined,
      market_legs: marketLegs,
      rationale: asString(row.rationale) ?? undefined,
    });
  }
  return scenarios;
}

type DateNavArrowProps = {
  direction: 'older' | 'newer';
  disabled: boolean;
  label: string;
  onClick: () => void;
};

type SectorBucketCard = {
  bucketLabel: string;
  sector: string;
  performance?: string | null;
  narrative: string;
};

function SectorBucketCards({
  title,
  items,
  footer,
}: {
  title: string;
  items: SectorBucketCard[];
  footer?: string | null;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-8 pt-8 border-t border-gray-100">
      <p className="text-sm font-medium text-gray-500 mb-3">{title}</p>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={`${item.bucketLabel}-${item.sector}`} className="rounded-2xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500 mb-1">{item.bucketLabel}</p>
            <div className="flex flex-wrap items-baseline gap-2 mb-1">
              <p className="font-medium text-gray-900">{formatSectorOutlookLabel(item.sector)}</p>
              {item.performance ? (
                <span className={`text-sm font-medium ${pctChipClass(parsePerformancePct(item.performance))}`}>
                  {item.performance}
                </span>
              ) : null}
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{displayProse(item.narrative)}</p>
          </div>
        ))}
      </div>
      {footer ? <p className="text-sm text-gray-600 leading-relaxed mt-4">{displayProse(footer)}</p> : null}
    </div>
  );
}

function buildSectorSelectionCards(
  sectorSel: Record<string, unknown> | null,
  predictedSectors: Record<string, unknown> | null,
): { items: SectorBucketCard[]; ranking: string | null } {
  if (!sectorSel) return { items: [], ranking: null };
  const ranking = asString(sectorSel.ranking_logic);
  const buckets = [
    { title: 'Top sectors', key: 'top_3' },
    { title: 'Bottom sectors', key: 'bottom_3' },
  ] as const;
  const items: SectorBucketCard[] = [];
  for (const bucket of buckets) {
    const raw = sectorSel[bucket.key];
    if (!Array.isArray(raw)) continue;
    for (const entry of raw) {
      const row = asRecord(entry);
      const sector = asString(row?.sector);
      const why = asString(row?.why);
      if (!sector || !why) continue;
      const sectorRow = asRecord(predictedSectors?.[sector]);
      const pct = asNumber(sectorRow?.predicted_change_pct);
      items.push({
        bucketLabel: bucket.title,
        sector,
        performance: pct != null ? formatPct(pct) : null,
        narrative: why,
      });
    }
  }
  return { items, ranking };
}

function parsePerformancePct(value: string): number {
  const cleaned = value.replace('%', '').replace('+', '').trim();
  const num = Number.parseFloat(cleaned);
  return Number.isFinite(num) ? num : 0;
}

function DateNavArrow({ direction, disabled, label, onClick }: DateNavArrowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full border-[3px] border-gray-900 bg-white text-gray-900 shadow-[0_6px_20px_rgba(0,0,0,0.18)] transition-all hover:bg-gray-50 hover:shadow-[0_8px_24px_rgba(0,0,0,0.22)] active:scale-95 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-300 disabled:shadow-none"
    >
      {direction === 'older' ? (
        <ChevronLeft className="h-9 w-9 sm:h-10 sm:w-10 stroke-[3]" />
      ) : (
        <ChevronRight className="h-9 w-9 sm:h-10 sm:w-10 stroke-[3]" />
      )}
    </button>
  );
}

const PREDICTION_DATE_LIST_LIMIT = 120;

export function MarketPredictionPage() {
  const navigate = useNavigate();
  const { referenceDate: routeReferenceDate } = useParams<{ referenceDate?: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<MarketPredictionResponse | null>(null);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [reasoningExpanded, setReasoningExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    marketPredictionService.list(PREDICTION_DATE_LIST_LIMIT).then((response) => {
      if (cancelled || !response?.predictions?.length) return;
      const dates = [...new Set(response.predictions.map((row) => row.reference_date))].sort();
      setAvailableDates(dates);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const prediction = routeReferenceDate
          ? await marketPredictionService.getByDate(routeReferenceDate)
          : await marketPredictionService.getLatest();
        if (!cancelled) {
          setData(prediction);
          if (prediction?.available_dates?.length) {
            setAvailableDates(
              [...new Set(prediction.available_dates)].sort(),
            );
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [routeReferenceDate]);

  const summary = data?.summary;
  const prediction = data?.prediction;
  const scorecard = data?.scorecard;
  const analogueAnalysis = data?.analogue_analysis ?? null;
  const hasAnalogueOutlook = Boolean(analogueAnalysis);
  const analysisExcerpt = data?.market_analysis_excerpt;
  const dominantThemes = analysisExcerpt?.dominant_themes ?? [];

  const nextSession = useMemo(
    () => resolveNextSessionBlock(prediction as Record<string, unknown> | null | undefined),
    [prediction],
  );

  const forwardDrivers = useMemo(
    (): MarketAnalysisTheme[] =>
      forwardDriversFromComposerPayload(prediction as Record<string, unknown> | null | undefined),
    [prediction],
  );
  const marketMeta = useMemo(() => asRecord(nextSession?.market), [nextSession]);
  const analogAnalysis = useMemo(() => asRecord(prediction?.analog_analysis), [prediction]);
  const closestAnalogLegacy = useMemo(() => asRecord(analogAnalysis?.closest_analog), [analogAnalysis]);
  const sectorOutlook = useMemo(() => asRecord(prediction?.sector_outlook), [prediction]);
  const crossAsset = useMemo(() => asRecord(prediction?.cross_asset_predictions), [prediction]);
  const invalidation = useMemo(() => asRecord(prediction?.invalidation), [prediction]);
  const weekly = useMemo(() => asRecord(prediction?.weekly_index_outlook), [prediction]);
  const marketView = useMemo(() => asRecord(prediction?.market_view), [prediction]);
  const predictionReasoning = useMemo(() => asRecord(prediction?.prediction_reasoning), [prediction]);
  const narrativeOverlay = useMemo(() => asRecord(prediction?.narrative_overlay), [prediction]);
  const macroCatalyst = useMemo(() => asRecord(prediction?.macro_catalyst_forecast), [prediction]);
  const surpriseForecast = useMemo(() => asRecord(macroCatalyst?.surprise_forecast), [macroCatalyst]);

  const topAnalogs = useMemo((): TopAnalog[] => {
    const raw = analogAnalysis?.top_analogs;
    if (!Array.isArray(raw)) return [];
    const analogs: TopAnalog[] = [];
    for (const item of raw) {
      const row = asRecord(item);
      if (!row) continue;
      const date = asString(row.date);
      const similarity = asNumber(row.similarity);
      if (!date || similarity == null) continue;
      analogs.push({
        date,
        similarity,
        next_date: asString(row.next_date) ?? undefined,
        next_SPY: asNumber(row.next_SPY) ?? undefined,
        next_QQQ: asNumber(row.next_QQQ) ?? undefined,
        next_DIA: asNumber(row.next_DIA) ?? undefined,
        next_IWM: asNumber(row.next_IWM) ?? undefined,
      });
    }
    return analogs;
  }, [analogAnalysis]);

  const closestAnalog = topAnalogs[0] ?? null;

  const indexDetails = useMemo(
    () => parseIndexDetails(asRecord(nextSession?.indices)),
    [nextSession],
  );

  const baseCaseRationale = asString(asRecord(asRecord(nextSession?.scenarios)?.base_case)?.rationale);
  const todayContext =
    analysisExcerpt?.excerpt ?? analysisExcerpt?.overall_market_direction ?? null;

  const sessionSectorCards = useMemo((): SectorBucketCard[] => {
    const sectorPerf = analysisExcerpt?.sector_performance;
    if (!sectorPerf) return [];
    const buckets = [
      { title: 'Top sectors', rows: sectorPerf.top_3 ?? [] },
      { title: 'Bottom sectors', rows: sectorPerf.bottom_3 ?? [] },
    ] as const;
    const items: SectorBucketCard[] = [];
    for (const bucket of buckets) {
      for (const row of bucket.rows) {
        const sector = asString(row.sector);
        const narrative = asString(row.narrative);
        if (!sector || !narrative) continue;
        items.push({
          bucketLabel: bucket.title,
          sector,
          performance: asString(row.performance),
          narrative,
        });
      }
    }
    return items;
  }, [analysisExcerpt]);

  const forecastSectorSelection = useMemo(
    () =>
      buildSectorSelectionCards(
        asRecord(predictionReasoning?.sector_selections),
        asRecord(nextSession?.sectors),
      ),
    [predictionReasoning, nextSession],
  );

  const predictionRationale = buildMarketPredictionThesis({
    marketViewThesis: asString(marketView?.current_thesis),
    weeklyThesis: asString(weekly?.thesis),
    baseCaseRationale,
    surpriseRationale: asString(surpriseForecast?.rationale),
    narrativeScenarioTilt:
      asString(narrativeOverlay?.scenario_tilt) ?? asString(marketMeta?.narrative_scenario_tilt),
    rotationExpectation: asString(narrativeOverlay?.rotation_expectation),
    overallDirection: summary?.overall_direction ?? asString(marketMeta?.overall_direction),
    predictedSpyPct: summary?.predicted_spy_change_pct ?? asNumber(marketMeta?.predicted_spy_change_pct),
    catalystEvent: summary?.catalyst_event ?? asString(surpriseForecast?.event) ?? asString(marketMeta?.catalyst_event),
    catalystBucket:
      summary?.catalyst_surprise_bucket ??
      asString(surpriseForecast?.surprise_bucket) ??
      asString(marketMeta?.catalyst_surprise_bucket),
  });

  const todayIndexTags = useMemo(() => {
    const ref = asRecord(prediction?.reference_closes);
    if (!ref) return [];
    const tags: { ticker: string; change_pct: number }[] = [];
    for (const ticker of ['SPY', 'QQQ', 'DIA', 'IWM'] as const) {
      const row = asRecord(ref[ticker]);
      const pct = asNumber(row?.prior_day_pct);
      if (pct != null) tags.push({ ticker, change_pct: pct });
    }
    return tags;
  }, [prediction]);

  const sessionReferenceDate =
    summary?.reference_date ??
    asString(nextSession?.reference_date) ??
    data?.reference_date;
  const predictionTargetDate = summary?.target_date ?? asString(nextSession?.target_date);

  const activeReferenceDate =
    sessionReferenceDate ?? routeReferenceDate ?? data?.reference_date ?? null;

  const dateNavigation = useMemo(() => {
    if (!activeReferenceDate || availableDates.length === 0) {
      return { olderDate: null as string | null, newerDate: null as string | null, position: null as number | null };
    }

    const currentIndex = availableDates.indexOf(activeReferenceDate);
    if (currentIndex === -1) {
      return { olderDate: null, newerDate: null, position: null };
    }

    return {
      olderDate: currentIndex > 0 ? availableDates[currentIndex - 1] : null,
      newerDate:
        currentIndex < availableDates.length - 1 ? availableDates[currentIndex + 1] : null,
      position: currentIndex + 1,
    };
  }, [activeReferenceDate, availableDates]);

  const goToReferenceDate = (date: string) => {
    navigate(`/market/prediction/${date}`);
  };

  const legacyScenarios = useMemo((): ScenarioRow[] => {
    const raw = resolveComposerScenarios(prediction as Record<string, unknown> | null | undefined);
    if (!raw) return [];
    const labels: Record<string, string> = {
      bull_case: 'Bull',
      base_case: 'Base',
      bear_case: 'Bear',
    };
    const rows: ScenarioRow[] = [];
    for (const [key, label] of Object.entries(labels)) {
      const row = asRecord(raw[key]);
      if (!row) continue;
      rows.push({
        label,
        SPY_pct: asNumber(row.SPY_pct) ?? undefined,
        QQQ_pct: asNumber(row.QQQ_pct) ?? undefined,
        DIA_pct: asNumber(row.DIA_pct) ?? undefined,
        IWM_pct: asNumber(row.IWM_pct) ?? undefined,
        rationale: asString(row.rationale) ?? undefined,
      });
    }
    return rows;
  }, [nextSession]);

  const catalystScenarios = useMemo(() => {
    const fromMacro = parseCatalystScenarios(asRecord(macroCatalyst?.scenarios));
    if (fromMacro.length > 0) return fromMacro;
    return parseCatalystScenarios(asRecord(prediction?.catalyst_scenarios));
  }, [macroCatalyst, prediction]);

  const indexTags = summary?.index_forecasts?.length
    ? summary.index_forecasts
    : indexDetails.length > 0
      ? indexDetails.map((row) => ({
          ticker: row.ticker,
          predicted_change_pct: row.predicted_change_pct,
        }))
      : summary?.predicted_spy_change_pct != null
        ? [{ ticker: 'SPY', predicted_change_pct: summary.predicted_spy_change_pct }]
        : [];

  const catalysts = Array.isArray(prediction?.catalysts_tuesday)
    ? (prediction.catalysts_tuesday as Array<Record<string, unknown>>)
    : [];

  const narrativeParallels = asStringArray(closestAnalogLegacy?.narrative_parallels);
  const analogNextOutcome = asRecord(closestAnalogLegacy?.next_session_outcome);
  const confidenceScore = summary?.confidence_score ?? asNumber(marketMeta?.confidence_score);
  const overallDirection = summary?.overall_direction ?? asString(marketMeta?.overall_direction);
  const scenarioTilt =
    summary?.narrative_scenario_tilt ??
    asString(narrativeOverlay?.scenario_tilt) ??
    asString(marketMeta?.narrative_scenario_tilt);
  const catalystEvent = summary?.catalyst_event ?? asString(surpriseForecast?.event) ?? asString(marketMeta?.catalyst_event);
  const catalystBucket =
    summary?.catalyst_surprise_bucket ??
    asString(surpriseForecast?.surprise_bucket) ??
    asString(marketMeta?.catalyst_surprise_bucket);

  const outperformSectors =
    asStringArray(sectorOutlook?.outperform).length > 0
      ? asStringArray(sectorOutlook?.outperform)
      : asStringArray(sectorOutlook?.outperform_symbols);
  const underperformSectors =
    asStringArray(sectorOutlook?.underperform).length > 0
      ? asStringArray(sectorOutlook?.underperform)
      : asStringArray(sectorOutlook?.underperform_symbols);

  const leadingSignals = asStringArray(surpriseForecast?.leading_signals);

  const renderLegChips = (legs: Record<string, number>, tickers: string[]) => (
    <div className="flex flex-wrap gap-2">
      {tickers.map((ticker) => {
        const pct = legs[ticker];
        if (pct == null) return null;
        return (
          <span key={ticker} className={`rounded-lg px-2.5 py-1 text-xs ${pctChipClass(pct)}`}>
            {formatAssetLegLabel(ticker)} {formatPct(pct)}
          </span>
        );
      })}
      {legs.yield_bps != null ? (
        <span className={`rounded-lg px-2.5 py-1 text-xs ${pctChipClass(legs.yield_bps)}`}>
          10Y {formatBps(legs.yield_bps)}
        </span>
      ) : null}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="shrink-0 bg-white border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto w-full px-6 py-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center shrink-0">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="tracking-tight text-gray-900">Market Prediction</h2>
              {sessionReferenceDate && predictionTargetDate ? (
                <p className="text-sm text-gray-500 truncate">
                  {formatDateLabel(sessionReferenceDate)} recap · {formatDateLabel(predictionTargetDate)} forecast
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </motion.header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8 md:py-10 flex flex-col gap-6">
        {loading ? (
          <div className="flex flex-1 min-h-[calc(100vh-8rem)] items-center justify-center rounded-3xl border border-gray-200 bg-white p-10">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin mr-3" />
            <span className="text-gray-500">Loading market prediction...</span>
          </div>
        ) : !data ? (
          <div className="flex flex-1 min-h-[calc(100vh-8rem)] items-center justify-center rounded-3xl border border-gray-200 bg-white p-10 text-center">
            <div>
              <p className="text-gray-500">No market prediction available yet.</p>
              <p className="text-gray-400 text-sm mt-1">Check back after the next market analysis run.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Today's session recap (reference date) */}
            <div className="relative pt-10 sm:pt-12">
              <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-between px-2 sm:px-6">
                <div className="pointer-events-auto">
                  <DateNavArrow
                    direction="older"
                    disabled={!dateNavigation.olderDate || loading}
                    label={
                      dateNavigation.olderDate
                        ? `Previous session: ${formatDateLabel(dateNavigation.olderDate)}`
                        : 'No earlier prediction'
                    }
                    onClick={() => {
                      if (dateNavigation.olderDate) goToReferenceDate(dateNavigation.olderDate);
                    }}
                  />
                </div>
                <div className="pointer-events-auto">
                  <DateNavArrow
                    direction="newer"
                    disabled={!dateNavigation.newerDate || loading}
                    label={
                      dateNavigation.newerDate
                        ? `Next session: ${formatDateLabel(dateNavigation.newerDate)}`
                        : 'Already on latest prediction'
                    }
                    onClick={() => {
                      if (dateNavigation.newerDate) goToReferenceDate(dateNavigation.newerDate);
                    }}
                  />
                </div>
              </div>

            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 lg:p-12 shadow-sm"
            >
              <p className="text-sm font-medium text-gray-500 mb-3">Today&apos;s session</p>
              <div className="border-b border-gray-100 pb-8 mb-8">
                <h3 className="text-3xl md:text-4xl text-gray-900 tracking-tight mb-4">
                  {formatDateLabel(sessionReferenceDate)}
                </h3>
                {todayIndexTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {todayIndexTags.map((row) => (
                      <span
                        key={row.ticker}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium ${pctChipClass(row.change_pct)}`}
                      >
                        {row.ticker} {formatPct(row.change_pct)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {todayContext ? (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-3">
                    Session recap
                    {analysisExcerpt?.confidence_level
                      ? ` · ${humanizePredictionLabel(analysisExcerpt.confidence_level)} confidence`
                      : ''}
                  </p>
                  <p className="max-w-5xl text-lg md:text-xl text-gray-800 leading-[1.75] whitespace-pre-line">
                    {todayContext}
                  </p>
                </div>
              ) : (
                <p className="text-lg text-gray-500 italic">No session analysis available yet.</p>
              )}

              <MarketThemeCards
                title="What drove today"
                themes={dominantThemes}
                className="mt-8 pt-8 border-t border-gray-100"
              />

              <SectorBucketCards title="Sector performance" items={sessionSectorCards} />
            </motion.section>
            </div>

            {/* Market outlook — structured analogue narrative when available */}
            {hasAnalogueOutlook && analogueAnalysis ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <MarketAnalogueAnalysisPanel analysis={analogueAnalysis} />
              </motion.div>
            ) : (
              <>
            {/* Tomorrow's prediction (target date) */}
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 lg:p-12 shadow-sm"
            >
              <p className="text-sm font-medium text-gray-500 mb-3">Next session prediction</p>
              <div className="border-b border-gray-100 pb-8 mb-8">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <h3 className="text-3xl md:text-4xl text-gray-900 tracking-tight">
                    {formatDateLabel(predictionTargetDate)}
                  </h3>
                  {overallDirection ? (
                    <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm capitalize text-gray-700">
                      {overallDirection}
                    </span>
                  ) : null}
                  {confidenceScore != null ? (
                    <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">
                      Confidence {(confidenceScore * 100).toFixed(0)}%
                    </span>
                  ) : null}
                  {summary?.regime || scenarioTilt ? (
                    <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">
                      {humanizePredictionLabel(summary?.regime ?? scenarioTilt)}
                    </span>
                  ) : null}
                  {catalystEvent ? (
                    <span className="rounded-lg bg-amber-50 px-3 py-1 text-sm text-amber-800">
                      {catalystEvent}
                      {catalystBucket ? ` · ${humanizePredictionLabel(catalystBucket)}` : ''}
                    </span>
                  ) : null}
                </div>

                {indexTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {indexTags.map((row) => (
                      <span
                        key={row.ticker}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium ${pctChipClass(row.predicted_change_pct)}`}
                      >
                        {row.ticker} {formatPct(row.predicted_change_pct)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {predictionRationale ? (
                <p className="max-w-5xl text-lg text-gray-800 leading-[1.75] mb-6">{predictionRationale}</p>
              ) : null}

              {displayProse(marketView?.next_likely_move) ? (
                <div className="mb-6 rounded-2xl border border-gray-100 bg-gray-50 p-5">
                  <p className="text-sm font-medium text-gray-500 mb-2">Next likely move</p>
                  <p className="text-gray-800 leading-relaxed">{displayProse(marketView?.next_likely_move)}</p>
                </div>
              ) : null}

              <SectorBucketCards
                title="Sector selections"
                items={forecastSectorSelection.items}
                footer={forecastSectorSelection.ranking}
              />

              <MarketThemeCards
                title={`What will drive ${formatDateLabel(predictionTargetDate)}`}
                themes={forwardDrivers}
                className={forwardDrivers.length > 0 ? 'mt-8 pt-8 border-t border-gray-100' : ''}
              />
            </motion.section>

            {indexDetails.length > 0 ? (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 }}
                className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 shadow-sm"
              >
                <h4 className="text-xl text-gray-900 mb-1">Index forecasts</h4>
                <p className="text-sm text-gray-500 mb-6">
                  Predicted moves for {formatDateLabel(predictionTargetDate)}
                </p>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {indexDetails.map((row) => (
                    <div key={row.ticker} className="rounded-2xl border border-gray-100 p-5">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-medium text-gray-900">{row.ticker}</p>
                        {row.direction ? (
                          <span className="text-xs capitalize text-gray-500">{row.direction}</span>
                        ) : null}
                      </div>
                      <p className={`text-2xl font-medium ${row.predicted_change_pct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {formatPct(row.predicted_change_pct)}
                      </p>
                      {row.predicted_close != null ? (
                        <p className="text-sm text-gray-500 mt-1">Close ~{row.predicted_close.toFixed(2)}</p>
                      ) : null}
                      {row.predicted_range_pct ? (
                        <p className="text-xs text-gray-400 mt-2">
                          Range {formatPct(row.predicted_range_pct[0])} to {formatPct(row.predicted_range_pct[1])}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </motion.section>
            ) : null}

            {catalystScenarios.length > 0 ? (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.065 }}
                className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 shadow-sm"
              >
                <h4 className="text-xl text-gray-900 mb-2">Catalyst scenarios</h4>
                {catalystEvent ? (
                  <p className="text-gray-600 mb-6">
                    {catalystEvent}
                    {asNumber(surpriseForecast?.consensus) != null
                      ? ` · consensus ${asNumber(surpriseForecast?.consensus)}`
                      : ''}
                    {asNumber(surpriseForecast?.predicted_value) != null
                      ? ` · model ${asNumber(surpriseForecast?.predicted_value)?.toFixed(2)}`
                      : ''}
                  </p>
                ) : null}
                <div className="grid gap-4 md:grid-cols-3">
                  {catalystScenarios.map((scenario) => (
                    <div key={scenario.key} className="rounded-2xl border border-gray-100 p-5">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-medium text-gray-900">{scenario.label}</p>
                        {scenario.surprise_bucket ? (
                          <span className="text-xs capitalize text-gray-500">{scenario.surprise_bucket}</span>
                        ) : null}
                      </div>
                      {renderLegChips(scenario.market_legs, ['SPY', 'QQQ', 'DIA', 'IWM', 'XLK', 'XLE', 'USO', 'GLD'])}
                      {scenario.rationale ? (
                        <p className="text-sm text-gray-600 leading-relaxed mt-4">
                          {displayProse(scenario.rationale)}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
                {leadingSignals.length > 0 ? (
                  <ul className="mt-6 space-y-2 text-sm text-gray-600">
                    {leadingSignals.map((signal) => (
                      <li key={signal} className="flex gap-2">
                        <span className="text-gray-400">·</span>
                        <span>{expandSectorTickersInText(signal)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </motion.section>
            ) : legacyScenarios.length > 0 ? (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.065 }}
                className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 shadow-sm"
              >
                <h4 className="text-xl text-gray-900 mb-6">Scenarios</h4>
                <div className="grid gap-4 md:grid-cols-3">
                  {legacyScenarios.map((scenario) => (
                    <div key={scenario.label} className="rounded-2xl border border-gray-100 p-5">
                      <p className="font-medium text-gray-900 mb-3">{scenario.label}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(['SPY', 'QQQ', 'DIA', 'IWM'] as const).map((ticker) => {
                          const pct = scenario[`${ticker}_pct`];
                          if (pct == null) return null;
                          return (
                            <span key={ticker} className={`rounded-lg px-2.5 py-1 text-xs ${pctChipClass(pct)}`}>
                              {ticker} {formatPct(pct)}
                            </span>
                          );
                        })}
                      </div>
                      {scenario.rationale ? (
                        <p className="text-sm text-gray-600 leading-relaxed">{displayProse(scenario.rationale)}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </motion.section>
            ) : null}

            {predictionReasoning ? (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.07 }}
                className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden"
              >
                {/* Collapsible header */}
                <button
                  type="button"
                  onClick={() => setReasoningExpanded((v) => !v)}
                  className="w-full flex items-center justify-between gap-4 p-8 md:p-10 text-left hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <h4 className="text-xl text-gray-900">How this forecast was built</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Model reasoning — what signals drove each call
                    </p>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200 ${reasoningExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {reasoningExpanded ? (
                  <div className="px-8 pb-8 md:px-10 md:pb-10 border-t border-gray-100 pt-6 space-y-6">
                    {displayProse(predictionReasoning.summary) ? (
                      <p className="text-gray-800 leading-relaxed">{displayProse(predictionReasoning.summary)}</p>
                    ) : null}

                    {displayProse(predictionReasoning.confidence_explanation) ? (
                      <div className="rounded-2xl bg-gray-50 p-5">
                        <p className="text-sm font-medium text-gray-500 mb-2">Confidence</p>
                        <p className="text-gray-700 leading-relaxed">
                          {displayProse(predictionReasoning.confidence_explanation)}
                        </p>
                      </div>
                    ) : null}

                    {(() => {
                      const indexCalls = asRecord(predictionReasoning.index_calls);
                      if (!indexCalls) return null;
                      const rows = (['SPY', 'QQQ', 'DIA', 'IWM'] as const)
                        .map((ticker) => {
                          const row = asRecord(indexCalls[ticker]);
                          const why = asString(row?.why);
                          if (!why) return null;
                          const inputs = asStringArray(row?.key_inputs).filter(
                            (input) => !isInternalKeyInput(input),
                          );
                          return { ticker, why, inputs };
                        })
                        .filter(
                          (row): row is { ticker: 'SPY' | 'QQQ' | 'DIA' | 'IWM'; why: string; inputs: string[] } =>
                            row != null,
                        );
                      if (rows.length === 0) return null;
                      return (
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-3">Index calls</p>
                          <div className="grid gap-3 md:grid-cols-2">
                            {rows.map((row) => (
                              <div key={row.ticker} className="rounded-2xl border border-gray-100 p-4">
                                <div className="mb-2">
                                  <p className="font-medium text-gray-900">{row.ticker}</p>
                                  {INDEX_ETF_LABELS[row.ticker] ? (
                                    <p className="text-xs text-gray-500">{INDEX_ETF_LABELS[row.ticker]}</p>
                                  ) : null}
                                </div>
                                <p className="text-sm text-gray-700 leading-relaxed">{displayProse(row.why)}</p>
                                {row.inputs.length > 0 ? (
                                  <ul className="mt-3 space-y-1 text-xs text-gray-500">
                                    {row.inputs.map((input) => (
                                      <li key={input} className="flex gap-2">
                                        <span>·</span>
                                        <span>{expandSectorTickersInText(input)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {(() => {
                      const rotation = asRecord(predictionReasoning.rotation_decision);
                      if (!rotation) return null;
                      const regime = asString(rotation.regime);
                      const why = asString(rotation.why);
                      if (!regime && !why) return null;
                      return (
                        <div className="rounded-2xl border border-gray-100 p-5">
                          <p className="text-sm font-medium text-gray-500 mb-2">Sector rotation</p>
                          {regime ? (
                            <p className="text-sm font-medium text-gray-900 mb-2 capitalize">
                              {humanizePredictionLabel(regime)}
                            </p>
                          ) : null}
                          {why ? <p className="text-sm text-gray-700 leading-relaxed">{displayProse(why)}</p> : null}
                        </div>
                      );
                    })()}

                    {(() => {
                      const items = asStringArray(predictionReasoning.conflicts_resolved).filter(
                        (item) => item.trim().length > 0,
                      );
                      if (items.length === 0) return null;
                      return (
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-3">Where signals conflicted</p>
                          <ul className="space-y-2 text-sm text-gray-600">
                            {items.map((item) => (
                              <li key={item} className="flex gap-2">
                                <span className="text-gray-400 shrink-0">·</span>
                                <span>{displayProse(item)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                ) : null}
              </motion.section>
            ) : null}

            {closestAnalog || closestAnalogLegacy ? (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 shadow-sm"
              >
                <h4 className="text-xl text-gray-900 mb-2">Closest historical analog</h4>
                <p className="text-gray-600 mb-6">
                  {formatDateLabel(closestAnalog?.date ?? asString(closestAnalogLegacy?.date))}
                  {(closestAnalog?.similarity ?? asNumber(closestAnalogLegacy?.similarity)) != null
                    ? ` · ${((closestAnalog?.similarity ?? asNumber(closestAnalogLegacy?.similarity)!) * 100).toFixed(1)}% match`
                    : ''}
                  {asNumber(analogAnalysis?.sessions_scanned) != null
                    ? ` · scanned ${asNumber(analogAnalysis?.sessions_scanned)} sessions`
                    : ''}
                </p>

                {narrativeParallels.length > 0 ? (
                  <ul className="mb-6 space-y-2 text-gray-700">
                    {narrativeParallels.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="text-gray-400">·</span>
                        <span>{expandSectorTickersInText(item)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {closestAnalog ? (
                  <div className="rounded-2xl bg-gray-50 p-5">
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      What happened next ({formatDateLabel(closestAnalog.next_date)})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(['SPY', 'QQQ', 'DIA', 'IWM'] as const).map((ticker) => {
                        const pct = closestAnalog[`next_${ticker}` as keyof TopAnalog] as number | undefined;
                        if (pct == null) return null;
                        return (
                          <span key={ticker} className={`rounded-lg px-3 py-1 text-sm ${pctChipClass(pct)}`}>
                            {ticker} {formatPct(pct)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ) : analogNextOutcome ? (
                  <div className="rounded-2xl bg-gray-50 p-5">
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      What happened next ({formatDateLabel(asString(analogNextOutcome.date))})
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(['SPY', 'QQQ', 'DIA', 'IWM'] as const).map((ticker) => {
                        const pct = asNumber(analogNextOutcome[`${ticker}_pct`]);
                        if (pct == null) return null;
                        return (
                          <span key={ticker} className={`rounded-lg px-3 py-1 text-sm ${pctChipClass(pct)}`}>
                            {ticker} {formatPct(pct)}
                          </span>
                        );
                      })}
                    </div>
                    {asString(analogNextOutcome.narrative) ? (
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {asString(analogNextOutcome.narrative)}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {topAnalogs.length > 1 ? (
                  <div className="mt-6">
                    <p className="text-sm font-medium text-gray-900 mb-3">Other top analogs</p>
                    <div className="space-y-2">
                      {topAnalogs.slice(1, 5).map((analog) => (
                        <div
                          key={analog.date}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-4 py-3 text-sm"
                        >
                          <span className="text-gray-900">{formatDateLabel(analog.date)}</span>
                          <span className="text-gray-500">{(analog.similarity * 100).toFixed(1)}% match</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </motion.section>
            ) : null}

            {(sectorOutlook || crossAsset || catalysts.length > 0 || invalidation) ? (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="grid gap-6 lg:grid-cols-2"
              >
                {sectorOutlook ? (
                  <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                    <h4 className="text-lg text-gray-900 mb-4">Sector outlook</h4>
                    <div className="space-y-4 text-sm">
                      <div>
                        <p className="text-gray-500 mb-2">Outperform</p>
                        <div className="flex flex-wrap gap-2">
                          {outperformSectors.map((sector) => (
                            <span key={sector} className="rounded-lg bg-emerald-50 px-2.5 py-1 text-emerald-800">
                              {formatSectorOutlookLabel(sector)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-2">Underperform</p>
                        <div className="flex flex-wrap gap-2">
                          {underperformSectors.map((sector) => (
                            <span key={sector} className="rounded-lg bg-rose-50 px-2.5 py-1 text-rose-800">
                              {formatSectorOutlookLabel(sector)}
                            </span>
                          ))}
                        </div>
                      </div>
                      {displayProse(sectorOutlook.rationale) ? (
                        <p className="text-gray-600 leading-relaxed">{displayProse(sectorOutlook.rationale)}</p>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {crossAsset ? (
                  <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                    <h4 className="text-lg text-gray-900 mb-4">Cross-asset</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {Object.entries(crossAsset).map(([key, value]) => {
                        if (typeof value === 'number') {
                          return (
                            <div key={key} className="rounded-xl bg-gray-50 px-4 py-3">
                              <p className="text-gray-500 uppercase text-xs">{humanizeKey(key)}</p>
                              <p className="text-gray-900 font-medium">{formatPct(value)}</p>
                            </div>
                          );
                        }
                        const row = asRecord(value);
                        if (!row) return null;
                        const level = asNumber(row.predicted_level);
                        const change = asNumber(row.predicted_change_pct);
                        const changeBps = asNumber(row.predicted_change_bps);
                        const direction = asString(row.direction);
                        return (
                          <div key={key} className="rounded-xl bg-gray-50 px-4 py-3">
                            <p className="text-gray-500 uppercase text-xs">{humanizeKey(key)}</p>
                            <p className="text-gray-900 font-medium capitalize">
                              {level != null ? level.toFixed(2) : direction ?? '—'}
                              {change != null ? ` (${formatPct(change)})` : ''}
                              {changeBps != null ? ` (${formatBps(changeBps)})` : ''}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {catalysts.length > 0 ? (
                  <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm lg:col-span-2">
                    <h4 className="text-lg text-gray-900 mb-4">Catalysts</h4>
                    <ul className="space-y-3">
                      {catalysts.map((item) => (
                        <li
                          key={`${item.event}-${item.release}`}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-4 py-3 text-sm"
                        >
                          <span className="text-gray-900">{asString(item.event)}</span>
                          <div className="flex items-center gap-2 text-gray-500">
                            {asString(item.significance) ? (
                              <span className="rounded-md bg-white px-2 py-0.5 capitalize">
                                {asString(item.significance)}
                              </span>
                            ) : null}
                            <span>{formatDateLabel(asString(item.release))}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {invalidation ? (
                  <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm lg:col-span-2">
                    <h4 className="text-lg text-gray-900 mb-4">Invalidation</h4>
                    <div className="grid gap-4 md:grid-cols-2 text-sm">
                      {displayProse(invalidation.bullish_flip) ? (
                        <div className="rounded-2xl bg-emerald-50 p-4">
                          <p className="font-medium text-emerald-900 mb-2">Bullish flip</p>
                          <p className="text-emerald-800 leading-relaxed">{displayProse(invalidation.bullish_flip)}</p>
                        </div>
                      ) : null}
                      {displayProse(invalidation.bearish_acceleration) ? (
                        <div className="rounded-2xl bg-rose-50 p-4">
                          <p className="font-medium text-rose-900 mb-2">Bearish acceleration</p>
                          <p className="text-rose-800 leading-relaxed">
                            {displayProse(invalidation.bearish_acceleration)}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </motion.section>
            ) : null}
              </>
            )}

            {scorecard ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.055 }}
              >
                <MarketPredictionScorecardPanel scorecard={scorecard} />
              </motion.div>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
